<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Convênios</h5>
      <b-button
        v-if="hasPermission('adicionar_convenios')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control"
        @input="filtrarConvenios"
      />
      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('desconto')">
              Desconto (%) {{ arrow('desconto') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('ativo')">
              Ativo {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="c in convenios" :key="c.id">
          <td>{{ c.nome }}</td>
          <td>
            {{ Number(c.desconto)
              .toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}%
          </td>
          <td>{{ c.ativo ? 'Sim' : 'Não' }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_convenios')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalharConvenio(c.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_convenios')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editarConvenio(c.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_convenios')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluirConvenio(c.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

// composable de listagem, paginação e ordenação
const {
  items: convenios,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/convenios`,
  { key: 'nome', order: 'asc' }
)

const pageOptions = [10, 15, 20, 25, 50]

const startRow = computed(() => (page.value - 1) * limit.value + 1)
const endRow   = computed(() => Math.min(page.value * limit.value, totalItems.value))

watch(page, (newVal, oldVal) => {
  if (newVal !== oldVal) load()
})
watch(limit, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    page.value = 1
    load()
  }
})

// handlers
function changeSort(field) {
  sort(field)
}
function changeLimit() {
  page.value = 1
  load()
}
function filtrarConvenios() {
  page.value = 1
  load()
}
function limparFiltros() {
  filters.value = { nome: '' }
  page.value = 1
  load()
}

// navegação e CRUD
function irParaCadastro() {
  router.push('/cadastros/convenios/novo')
}
function editarConvenio(id) {
  router.push(`/cadastros/convenios/${id}`)
}
function detalharConvenio(id) {
  router.push(`/cadastros/convenios/${id}/detalhar`)
}
async function excluirConvenio(id) {
  const ok = await proxy.$confirm('Tem certeza que deseja excluir este convênio?', 'Confirmação')
  if (!ok) return
  try {
    await axios.delete(`/cadastros/convenios/${id}`)
    load()
    proxy.$toast.success('Convênio excluído com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir convênio.'
    await proxy.$alert(msg, 'Erro')
  }
}

onMounted(async () => {
  await fetchPermissions()
  filters.value = { nome: '' }
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
/* esconde ícone nativo, deixando só o custom */
.bv-table-sort-icon {
  display: none !important;
}
</style>
